import { FC, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Button, Icon, ActivityIndicator } from '@kontentino/ui';
import { sanitizeTranslationError } from 'app/modules/posts/modules/translation/utils/sanitizeTranslationError';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';
import useInterval from 'app/hooks/useInterval';
import {
  TRANSLATION_LOADING_PHRASES,
  TRANSLATION_LOADING_PHRASES_DEFAULTS,
} from 'app/modules/posts/modules/translation/constants/translationLoadingPhrases';
import PostApi from 'api/post';
import { queryKey } from 'constants/queryKey';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import { TranslationJobResponse } from 'types/AiHubTranslation';
import { optimisticallySetTranslationJobProcessing } from './optimisticTranslationUpdate';
import LanguageSelectorPopover, {
  useLanguageLabel,
} from './LanguageSelectorPopover';

type Props = {
  postId: number;
  pageType?: number;
  showAlert?: boolean;
  canEdit?: boolean;
};

const TranslationStatusBadge: FC<Props> = ({
  postId,
  showAlert = false,
  canEdit = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { accountPlanType } = useSubscriptionInfo();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBrandProfileId, setSelectedBrandProfileId] = useState<
    number | null
  >(null);
  const prevTranslationStatusRef = useRef<string | undefined>();

  const { data, isLoading } = useQuery(
    queryKey.postTranslation(postId),
    () => PostApi.getTranslationJobByPostId(postId),
    {
      staleTime: 0,
      refetchOnMount: 'always',
      refetchInterval: (data) => {
        const status = data?.status;
        if (status === 'pending' || status === 'processing') {
          return 5000;
        }
        return false;
      },
      onSuccess: (data) => {
        prevTranslationStatusRef.current = data?.status;
      },
    },
  );

  const retryMutation = useMutation(
    (translation: TranslationJobResponse) => {
      if (!translation?.targetLanguage) {
        return Promise.reject(new Error('Missing target language for retry'));
      }
      return PostApi.createTranslationJob({
        localPostId: postId,
        globalPostId: translation.globalPostId ?? undefined,
        targetLanguage: translation.targetLanguage,
        translateText: true,
        translateFirstComment: true,
        translateImages: true,
        force: true,
        planType: accountPlanType,
      });
    },
    {
      onMutate: (translation) => {
        if (translation?.targetLanguage) {
          optimisticallySetTranslationJobProcessing(
            queryClient,
            postId,
            translation.targetLanguage,
          );
          prevTranslationStatusRef.current = 'processing';
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey.postTranslation(postId));
        toast(
          t('translation.retryStarted', { defaultValue: 'Retry started' }),
          'success',
        );
      },
      onError: (e: ApiClientError) => {
        toast(e?.userMessage || t('somethingWentWrong'), 'error');
      },
    },
  );

  const translateMutation = useMutation(
    (targetLanguage: string) =>
      PostApi.createTranslationJob({
        localPostId: postId,
        targetLanguage,
        translateText: true,
        translateFirstComment: true,
        translateImages: true,
        force: !!translation,
        brandProfileId: selectedBrandProfileId ?? undefined,
        planType: accountPlanType,
      }),
    {
      onMutate: (targetLanguage) => {
        optimisticallySetTranslationJobProcessing(
          queryClient,
          postId,
          targetLanguage,
        );
        prevTranslationStatusRef.current = 'processing';
      },
      onSuccess: () => {
        // Invalidate to trigger GET request for full job data
        // POST endpoint only returns { status: "pending" }
        queryClient.invalidateQueries(queryKey.postTranslation(postId));
        queryClient.invalidateQueries(queryKey.postDetail(postId));
        toast(
          t('translation.started', { defaultValue: 'Translation started' }),
          'success',
        );
      },
      onError: (e: ApiClientError) => {
        toast(e?.userMessage || t('somethingWentWrong'), 'error');
      },
    },
  );

  const translation = data as TranslationJobResponse | undefined;
  const status = translation?.status;
  const targetLanguage = translation?.targetLanguage ?? null;
  const languageLabel = useLanguageLabel(targetLanguage);

  const canRetry = status === 'failed' || !!translation?.canRetry;

  const handleLanguageSelect = (langValue: string | null) => {
    if (!langValue) return;
    translateMutation.mutate(langValue);
  };

  if (isLoading || translateMutation.isLoading) {
    if (!showAlert) {
      return <CompactProcessingIndicator />;
    }
  }

  if (!isLoading && !translation && !translateMutation.isLoading) {
    if (!canEdit) return null;
  }

  const translationInfoText = t('translation.chooseLanguageInfo', {
    defaultValue:
      'Choose a language to translate image text, post text and first comment if available.',
  });

  if (showAlert) {
    if (translateMutation.isLoading && !translation) {
      return (
        <TranslationAlert
          status="pending"
          languageLabel={t('translation.preparing', {
            defaultValue: 'Preparing...',
          })}
          canRetry={false}
          errorMessage={null}
          onRetry={() => {}}
          isRetrying={false}
          t={t}
        />
      );
    }
    if (!translation) return null;
    return (
      <div>
        <TranslationAlert
          status={translation.status}
          languageLabel={languageLabel}
          canRetry={canRetry}
          errorMessage={translation.errorMessage ?? null}
          onRetry={() => retryMutation.mutate(translation)}
          onRegenerate={
            translation.status === 'completed'
              ? () => setIsOpen(true)
              : undefined
          }
          isRetrying={retryMutation.isLoading}
          t={t}
        />
        {isOpen && (
          <div className="tw-mt-2">
            <LanguageSelectorPopover
              id={`badge-alert-${postId}`}
              isOpen={isOpen}
              onOpenChange={setIsOpen}
              selectedLanguage={targetLanguage}
              onLanguageSelect={handleLanguageSelect}
              showOriginalOption={false}
              isLoading={translateMutation.isLoading}
              showBrandProfileSelector
              selectedBrandProfileId={selectedBrandProfileId}
              onBrandProfileChange={setSelectedBrandProfileId}
              infoText={translationInfoText}
              trigger={
                <button
                  type="button"
                  className="tw-text-sm tw-text-primary-100"
                >
                  {t('translation.selectNewLanguage', {
                    defaultValue: 'Select language',
                  })}
                </button>
              }
            />
          </div>
        )}
      </div>
    );
  }

  const statusIndicator = () => {
    if (translateMutation.isLoading || retryMutation.isLoading) {
      return (
        <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grayscale-140">
          <ActivityIndicator className="tw-h-4 tw-w-4" />
          {t('translation.starting', { defaultValue: 'Starting...' })}
        </span>
      );
    }

    switch (status) {
      case 'pending':
      case 'processing':
        return <CompactProcessingIndicator languageLabel={languageLabel} />;
      case 'completed':
        return (
          <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grayscale-100">
            {languageLabel}
            {canEdit && <Icon icon={faChevronDown} size="sm" />}
          </span>
        );
      case 'failed':
        return (
          <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-danger-100">
            {languageLabel} ({t('failed', { defaultValue: 'Failed' })})
            {canEdit && <Icon icon={faChevronDown} size="sm" />}
          </span>
        );
      case 'restored':
        return (
          <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grayscale-100">
            {t('translation.original', { defaultValue: 'Original' })}
            {canEdit && <Icon icon={faChevronDown} size="sm" />}
          </span>
        );
      default:
        return (
          <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grayscale-100">
            {t('translation.original', { defaultValue: 'Original' })}
            {canEdit && <Icon icon={faChevronDown} size="sm" />}
          </span>
        );
    }
  };

  if (!canEdit) {
    return statusIndicator();
  }

  const isProcessing =
    status === 'pending' ||
    status === 'processing' ||
    translateMutation.isLoading ||
    retryMutation.isLoading;

  if (isProcessing) {
    return statusIndicator();
  }

  const retryFooter =
    translation?.status === 'failed' && canRetry ? (
      <Button
        variant="tertiary"
        size="small"
        onClick={() => {
          if (translation) {
            retryMutation.mutate(translation);
          }
          setIsOpen(false);
        }}
        isLoading={retryMutation.isLoading}
        className="tw-w-full"
      >
        {t('translation.retry', {
          defaultValue: 'Retry translation',
        })}
      </Button>
    ) : undefined;

  return (
    <LanguageSelectorPopover
      id={`badge-${postId}`}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      selectedLanguage={targetLanguage}
      onLanguageSelect={handleLanguageSelect}
      showOriginalOption={false}
      isLoading={translateMutation.isLoading}
      infoText={translationInfoText}
      showBrandProfileSelector
      selectedBrandProfileId={selectedBrandProfileId}
      onBrandProfileChange={setSelectedBrandProfileId}
      trigger={
        <button
          type="button"
          className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-grayscale-100 hover:tw-text-grayscale-180"
          data-cy={`translation-badge-trigger-${postId}`}
        >
          {statusIndicator()}
        </button>
      }
      footerContent={retryFooter}
    />
  );
};

type TranslationAlertProps = {
  status: string;
  languageLabel: string;
  canRetry: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  onRegenerate?: () => void;
  isRetrying: boolean;
  t: (key: string, options?: object) => string;
};

const TranslationAlert: FC<TranslationAlertProps> = ({
  status,
  languageLabel,
  canRetry,
  errorMessage,
  onRetry,
  onRegenerate,
  isRetrying,
  t,
}) => {
  switch (status) {
    case 'pending':
    case 'processing':
      return (
        <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-p-3">
          <ActivityIndicator className="tw-mt-1 tw-h-4 tw-w-4 tw-text-grayscale-140" />
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
              {t('translation.inProgress', {
                defaultValue: 'Translation in progress',
              })}
            </span>
            <span className="tw-text-sm tw-text-grayscale-100">
              {t('translation.translatingTo', {
                defaultValue: 'Translating to {{language}}...',
                language: languageLabel,
              })}
            </span>
          </div>
        </div>
      );

    case 'completed':
      return (
        <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-success-10 tw-p-3">
          <Icon icon={faCheckCircle} className="tw-mt-1 tw-text-success-100" />
          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
              {t('translation.completed', {
                defaultValue: 'Translation completed',
              })}
            </span>
            <span className="tw-text-sm tw-text-grayscale-100">
              {t('translation.translatedTo', {
                defaultValue: 'Translated to {{language}}',
                language: languageLabel,
              })}
            </span>
            {onRegenerate && (
              <Button
                variant="ghost"
                size="small"
                onClick={onRegenerate}
                className="tw-mt-1 tw-w-fit !tw-p-0 tw-text-sm tw-text-primary-100"
              >
                {t('translation.regenerate', {
                  defaultValue: 'Translate to different language',
                })}
              </Button>
            )}
          </div>
        </div>
      );

    case 'failed':
      return (
        <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-danger-10 tw-p-3">
          <Icon
            icon={faExclamationTriangle}
            className="tw-mt-1 tw-text-danger-100"
          />
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
              {t('translation.failed', { defaultValue: 'Translation failed' })}
            </span>
            {errorMessage && (
              <span className="tw-text-sm tw-text-grayscale-100">
                {sanitizeTranslationError(errorMessage) ?? errorMessage}
              </span>
            )}
            {canRetry && (
              <Button
                variant="ghost"
                size="small"
                onClick={onRetry}
                isLoading={isRetrying}
                className="tw-mt-1 tw-w-fit !tw-p-0 tw-text-sm tw-text-primary-100"
              >
                {t('translation.retry', { defaultValue: 'Retry translation' })}
              </Button>
            )}
          </div>
        </div>
      );

    case 'restored':
      return (
        <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-grayscale-5 tw-p-3">
          <Icon icon={faInfoCircle} className="tw-mt-1 tw-text-grayscale-100" />
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
              {t('translation.showingOriginal', {
                defaultValue: 'Showing original content',
              })}
            </span>
            <span className="tw-text-sm tw-text-grayscale-100">
              {t('translation.translationAvailable', {
                defaultValue: 'Translation to {{language}} is available',
                language: languageLabel,
              })}
            </span>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const CompactProcessingIndicator: FC<{ languageLabel?: string }> = ({
  languageLabel,
}) => {
  const { t } = useTranslation();
  const [phraseIndex, setPhraseIndex] = useState(0);

  useInterval(() => {
    setPhraseIndex((prev) => (prev + 1) % TRANSLATION_LOADING_PHRASES.length);
  }, 3000);

  const currentKey = TRANSLATION_LOADING_PHRASES[phraseIndex];

  return (
    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 tw-text-sm tw-text-grayscale-140">
      <ActivityIndicator className="tw-h-4 tw-w-4 tw-shrink-0" />
      <span className="tw-truncate">
        {languageLabel
          ? `${languageLabel} — ${t(currentKey, {
              defaultValue:
                TRANSLATION_LOADING_PHRASES_DEFAULTS[currentKey] ?? currentKey,
            })}`
          : t(currentKey, {
              defaultValue:
                TRANSLATION_LOADING_PHRASES_DEFAULTS[currentKey] ?? currentKey,
            })}
      </span>
    </span>
  );
};

export default TranslationStatusBadge;
